import { useEffect, useState } from 'react';
import { Vendor, VendorStatus, VendorCategory, ALL_CATEGORIES, VENDOR_STATUSES, ContactInfo, BillingAddress } from '../types';
import { vendorStore } from '../services/store';
import { useVendorRole } from '../hooks/useVendorRole';
import toast from 'react-hot-toast';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const STATUS_COLOR: Record<VendorStatus, [string, string]> = {
  Active:   ['#EAF3DE', '#3B6D11'],
  Inactive: ['#F1EFE8', '#5F5E5A'],
  Pending:  ['#FAEEDA', '#854F0B'],
  Archived: ['#F1EFE8', '#888780'],
};

function StatusBadge({ status }: { status: VendorStatus }) {
  const [bg, color] = STATUS_COLOR[status] ?? ['#F1EFE8', '#5F5E5A'];
  return (
    <span className="px-2 py-0.5 rounded-full text-[11px] font-medium" style={{ background: bg, color }}>
      {status}
    </span>
  );
}

function fmt(n: number) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
}

const INPUT = 'w-full px-2.5 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-[#1a5c2a]/20 focus:border-[#1a5c2a]';
const BTN_BASE = 'px-2.5 py-1.5 rounded-md text-xs font-medium cursor-pointer border transition-colors';

function Btn({ label, onClick, variant = 'ghost', disabled = false }: {
  label: string; onClick: () => void;
  variant?: 'primary' | 'ghost' | 'danger' | 'amber';
  disabled?: boolean;
}) {
  const cls: Record<string, string> = {
    primary: 'bg-[#1a5c2a] text-white border-transparent hover:bg-[#154a22]',
    ghost:   'bg-white text-gray-700 border-gray-200 hover:bg-gray-50',
    danger:  'bg-[#FCEBEB] text-[#A32D2D] border-[#F7C1C1] hover:bg-[#F7C1C1]',
    amber:   'bg-[#FAEEDA] text-[#854F0B] border-[#FAC775] hover:bg-[#FAC775]',
  };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`${BTN_BASE} ${cls[variant]} ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
    >
      {label}
    </button>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mb-3">
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      {children}
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[11px] font-semibold uppercase tracking-widest text-gray-400 mb-3 pt-2 border-t border-gray-100 mt-3">
      {children}
    </div>
  );
}

// ─── Category multi-select ────────────────────────────────────────────────────

function CategoryPicker({ value, onChange }: { value: VendorCategory[]; onChange: (v: VendorCategory[]) => void }) {
  const toggle = (cat: VendorCategory) => {
    onChange(value.includes(cat) ? value.filter(c => c !== cat) : [...value, cat]);
  };
  return (
    <div className="flex flex-wrap gap-1.5">
      {ALL_CATEGORIES.map(cat => {
        const active = value.includes(cat);
        return (
          <button
            key={cat}
            type="button"
            onClick={() => toggle(cat)}
            className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors cursor-pointer ${
              active
                ? 'bg-[#1a5c2a] text-white border-[#1a5c2a]'
                : 'bg-white text-gray-600 border-gray-200 hover:border-[#1a5c2a]'
            }`}
          >
            {cat}
          </button>
        );
      })}
    </div>
  );
}

// ─── Blank shapes ─────────────────────────────────────────────────────────────

function blankContact(): ContactInfo {
  return { name: '', title: '', phone: '', email: '' };
}
function blankAddress(): BillingAddress {
  return { street: '', city: '', state: '', zip: '' };
}

// ─── Vendor form ──────────────────────────────────────────────────────────────

interface VendorFormState {
  name: string;
  website: string;
  status: VendorStatus;
  categories: VendorCategory[];
  parentVendorId: string;
  rollupSpendToParent: boolean;
  primaryContact: ContactInfo;
  billingContact: ContactInfo;
  billingAddress: BillingAddress;
  budgetAmount: string;
  budgetType: 'Annual' | 'PerContract';
  thresholdAmount: string;
  thresholdType: 'Ceiling' | 'Awareness';
  vendorId: string;
  w9OnFile: boolean;
  insuranceCertOnFile: boolean;
  comments: string;
}

function blankForm(vendor?: Vendor | null): VendorFormState {
  return {
    name:                vendor?.name ?? '',
    website:             vendor?.website ?? '',
    status:              vendor?.status ?? 'Pending',
    categories:          vendor?.categories ?? [],
    parentVendorId:      vendor?.parentVendorId ?? '',
    rollupSpendToParent: vendor?.rollupSpendToParent ?? false,
    primaryContact:      vendor?.primaryContact ?? blankContact(),
    billingContact:      vendor?.billingContact ?? blankContact(),
    billingAddress:      vendor?.billingAddress ?? blankAddress(),
    budgetAmount:        vendor?.budget?.amount?.toString() ?? '',
    budgetType:          vendor?.budget?.type ?? 'Annual',
    thresholdAmount:     vendor?.spendThreshold?.amount?.toString() ?? '',
    thresholdType:       vendor?.spendThreshold?.type ?? 'Ceiling',
    vendorId:            vendor?.vendorId ?? '',
    w9OnFile:            vendor?.w9OnFile ?? false,
    insuranceCertOnFile: vendor?.insuranceCertOnFile ?? false,
    comments:            vendor?.comments ?? '',
  };
}

function VendorModal({
  vendor,
  allVendors,
  onSave,
  onCancel,
}: {
  vendor: Vendor | null;
  allVendors: Vendor[];
  onSave: () => void;
  onCancel: () => void;
}) {
  const [form, setForm] = useState<VendorFormState>(() => blankForm(vendor));

  const setField = (key: keyof VendorFormState) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
      setForm(f => ({ ...f, [key]: e.target.value }));

  const setContact = (which: 'primaryContact' | 'billingContact', key: keyof ContactInfo) =>
    (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm(f => ({ ...f, [which]: { ...f[which], [key]: e.target.value } }));

  const setAddress = (key: keyof BillingAddress) =>
    (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm(f => ({ ...f, billingAddress: { ...f.billingAddress, [key]: e.target.value } }));

  const save = () => {
    if (!form.name.trim()) { toast.error('Company name is required'); return; }
    if (form.categories.length === 0) { toast.error('Select at least one category'); return; }

    const payload: Omit<Vendor, 'id' | 'createdAt' | 'updatedAt' | 'totalSpend'> = {
      name:                form.name.trim(),
      website:             form.website.trim() || undefined,
      status:              form.status,
      categories:          form.categories,
      parentVendorId:      form.parentVendorId || undefined,
      rollupSpendToParent: form.rollupSpendToParent,
      primaryContact:      form.primaryContact,
      billingContact:      form.billingContact,
      billingAddress:      form.billingAddress,
      budget:              form.budgetAmount ? { amount: parseFloat(form.budgetAmount), type: form.budgetType } : undefined,
      spendThreshold:      form.thresholdAmount ? { amount: parseFloat(form.thresholdAmount), type: form.thresholdType } : undefined,
      vendorId:            form.vendorId || undefined,
      w9OnFile:            form.w9OnFile,
      insuranceCertOnFile: form.insuranceCertOnFile,
      comments:            form.comments || undefined,
    };

    if (vendor) {
      vendorStore.update(vendor.id, payload);
      toast.success('Vendor updated');
    } else {
      vendorStore.create(payload);
      toast.success('Vendor added');
    }
    onSave();
  };

  // Potential parents = all vendors except the current one and its children
  const potentialParents = allVendors.filter(v => v.id !== vendor?.id && !v.parentVendorId);

  return (
    <div className="flex flex-col h-full">
      <h2 className="text-[17px] font-semibold text-gray-900 mb-4">
        {vendor ? 'Edit Vendor' : 'Add Vendor'}
      </h2>

      <div className="flex-1 overflow-y-auto pr-1" style={{ maxHeight: 'calc(80vh - 120px)' }}>

        {/* Basic info */}
        <Field label="Company Name *">
          <input className={INPUT} value={form.name} onChange={setField('name')} placeholder="e.g. Acme Lumber Supplies" />
        </Field>
        <Field label="Website">
          <input className={INPUT} value={form.website} onChange={setField('website')} placeholder="https://..." />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Status">
            <select className={INPUT} value={form.status} onChange={setField('status')}>
              {VENDOR_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Parent Vendor">
            <select className={INPUT} value={form.parentVendorId} onChange={setField('parentVendorId')}>
              <option value="">— None (top-level) —</option>
              {potentialParents.map(v => (
                <option key={v.id} value={v.id}>{v.name}</option>
              ))}
            </select>
          </Field>
        </div>
        {form.parentVendorId && (
          <label className="flex items-center gap-2 text-sm text-gray-700 mb-3 cursor-pointer">
            <input
              type="checkbox"
              checked={form.rollupSpendToParent}
              onChange={e => setForm(f => ({ ...f, rollupSpendToParent: e.target.checked }))}
            />
            Roll up this vendor's spend to parent
          </label>
        )}

        {/* Categories */}
        <Field label="Categories *">
          <CategoryPicker value={form.categories} onChange={cats => setForm(f => ({ ...f, categories: cats }))} />
        </Field>

        {/* Primary Contact */}
        <SectionTitle>Primary Contact</SectionTitle>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Name"><input className={INPUT} value={form.primaryContact.name} onChange={setContact('primaryContact', 'name')} /></Field>
          <Field label="Title"><input className={INPUT} value={form.primaryContact.title} onChange={setContact('primaryContact', 'title')} /></Field>
          <Field label="Phone"><input className={INPUT} value={form.primaryContact.phone} onChange={setContact('primaryContact', 'phone')} /></Field>
          <Field label="Email"><input className={INPUT} type="email" value={form.primaryContact.email} onChange={setContact('primaryContact', 'email')} /></Field>
        </div>

        {/* Billing Contact */}
        <SectionTitle>Billing Contact</SectionTitle>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Name"><input className={INPUT} value={form.billingContact.name} onChange={setContact('billingContact', 'name')} /></Field>
          <Field label="Title"><input className={INPUT} value={form.billingContact.title} onChange={setContact('billingContact', 'title')} /></Field>
          <Field label="Phone"><input className={INPUT} value={form.billingContact.phone} onChange={setContact('billingContact', 'phone')} /></Field>
          <Field label="Email"><input className={INPUT} type="email" value={form.billingContact.email} onChange={setContact('billingContact', 'email')} /></Field>
        </div>

        {/* Billing Address */}
        <SectionTitle>Billing Address</SectionTitle>
        <Field label="Street">
          <input className={INPUT} value={form.billingAddress.street} onChange={setAddress('street')} />
        </Field>
        <div className="grid grid-cols-3 gap-3">
          <Field label="City"><input className={INPUT} value={form.billingAddress.city} onChange={setAddress('city')} /></Field>
          <Field label="State"><input className={INPUT} value={form.billingAddress.state} onChange={setAddress('state')} maxLength={2} placeholder="TX" /></Field>
          <Field label="ZIP"><input className={INPUT} value={form.billingAddress.zip} onChange={setAddress('zip')} /></Field>
        </div>

        {/* Financial */}
        <SectionTitle>Financial</SectionTitle>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Budget Amount ($)">
            <input className={INPUT} type="number" min="0" value={form.budgetAmount} onChange={setField('budgetAmount')} placeholder="0" />
          </Field>
          <Field label="Budget Type">
            <select className={INPUT} value={form.budgetType} onChange={setField('budgetType')}>
              <option value="Annual">Annual</option>
              <option value="PerContract">Per Contract</option>
            </select>
          </Field>
          <Field label="Spend Threshold ($)">
            <input className={INPUT} type="number" min="0" value={form.thresholdAmount} onChange={setField('thresholdAmount')} placeholder="0" />
          </Field>
          <Field label="Threshold Type">
            <select className={INPUT} value={form.thresholdType} onChange={setField('thresholdType')}>
              <option value="Ceiling">Ceiling (do not exceed)</option>
              <option value="Awareness">Awareness (notify only)</option>
            </select>
          </Field>
        </div>

        {/* Optional */}
        <SectionTitle>Optional Fields</SectionTitle>
        <Field label="Internal Vendor ID">
          <input className={INPUT} value={form.vendorId} onChange={setField('vendorId')} placeholder="e.g. V-001" />
        </Field>
        <div className="flex gap-6 mb-3">
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input type="checkbox" checked={form.w9OnFile} onChange={e => setForm(f => ({ ...f, w9OnFile: e.target.checked }))} />
            W9 on file
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input type="checkbox" checked={form.insuranceCertOnFile} onChange={e => setForm(f => ({ ...f, insuranceCertOnFile: e.target.checked }))} />
            Insurance certificate on file
          </label>
        </div>
        <Field label="Comments">
          <textarea className={`${INPUT} min-h-[72px] resize-y`} value={form.comments} onChange={setField('comments')} />
        </Field>
      </div>

      <div className="flex justify-end gap-2.5 pt-4 mt-2 border-t border-gray-100">
        <button onClick={onCancel} className="px-4 py-2 rounded-lg border border-gray-200 bg-white text-sm cursor-pointer">
          Cancel
        </button>
        <button onClick={save} className="px-4 py-2 rounded-lg bg-[#1a5c2a] text-white text-sm font-medium cursor-pointer hover:bg-[#154a22]">
          {vendor ? 'Save Changes' : 'Add Vendor'}
        </button>
      </div>
    </div>
  );
}

// ─── Archive confirm dialog ───────────────────────────────────────────────────

function ArchiveDialog({ vendor, onConfirm, onCancel }: { vendor: Vendor; onConfirm: () => void; onCancel: () => void }) {
  return (
    <div>
      <h2 className="text-[17px] font-semibold text-gray-900 mb-2">Archive vendor?</h2>
      <p className="text-sm text-gray-600 mb-1">
        <strong>{vendor.name}</strong> will be set to Archived status. No data is deleted.
      </p>
      <p className="text-xs text-gray-400 mb-5">You can restore it later by changing the status back to Active.</p>
      <div className="flex justify-end gap-2.5">
        <button onClick={onCancel} className="px-4 py-2 rounded-lg border border-gray-200 bg-white text-sm cursor-pointer">Cancel</button>
        <button onClick={onConfirm} className="px-4 py-2 rounded-lg bg-[#854F0B] text-white text-sm font-medium cursor-pointer hover:bg-[#633806]">Archive</button>
      </div>
    </div>
  );
}

// ─── Vendor card ──────────────────────────────────────────────────────────────

function VendorCard({
  vendor,
  children,
  onEdit,
  onArchive,
  canEdit,
  isChild = false,
}: {
  vendor: Vendor;
  children?: Vendor[];
  onEdit: (v: Vendor) => void;
  onArchive: (v: Vendor) => void;
  canEdit: boolean;
  isChild?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className={`bg-white rounded-xl shadow-sm overflow-hidden ${isChild ? 'ml-6 border-l-2 border-[#1a5c2a]/20' : ''}`}>
      <div
        className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => setExpanded(x => !x)}
      >
        {children && children.length > 0 && (
          <span className="text-[10px] text-gray-400 transition-transform duration-200 inline-block"
            style={{ transform: expanded ? 'rotate(90deg)' : 'none' }}>▶</span>
        )}
        {(!children || children.length === 0) && <span className="w-3" />}

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-[15px] text-gray-900">{vendor.name}</span>
            <StatusBadge status={vendor.status} />
            {vendor.categories.map(cat => (
              <span key={cat} className="px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded text-[10px] font-medium">{cat}</span>
            ))}
          </div>
          <div className="flex gap-4 mt-0.5 text-[11px] text-gray-400 flex-wrap">
            {vendor.primaryContact.name && <span>👤 {vendor.primaryContact.name}</span>}
            {vendor.primaryContact.email && <span>{vendor.primaryContact.email}</span>}
            {vendor.website && <a href={vendor.website} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} className="text-blue-500 hover:underline">{vendor.website}</a>}
          </div>
        </div>

        <div className="flex items-center gap-3 shrink-0">
          {vendor.totalSpend > 0 && (
            <div className="text-right">
              <div className="text-[11px] text-gray-400">Total spend</div>
              <div className="text-sm font-semibold text-gray-700">{fmt(vendor.totalSpend)}</div>
            </div>
          )}
          {vendor.budget && (
            <div className="text-right">
              <div className="text-[11px] text-gray-400">Budget</div>
              <div className="text-sm font-semibold text-gray-700">{fmt(vendor.budget.amount)}</div>
            </div>
          )}
          {canEdit && (
            <div className="flex gap-1.5" onClick={e => e.stopPropagation()}>
              <Btn label="Edit" onClick={() => onEdit(vendor)} />
              {vendor.status !== 'Archived' && (
                <Btn label="Archive" onClick={() => onArchive(vendor)} variant="amber" />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div className="border-t border-gray-100 px-4 py-3 bg-gray-50 text-sm">
          <div className="grid grid-cols-2 gap-x-8 gap-y-1.5 text-[12px]">
            {vendor.billingContact.name && (
              <>
                <div className="text-gray-500 font-medium">Billing contact</div>
                <div className="text-gray-700">{vendor.billingContact.name} · {vendor.billingContact.email}</div>
              </>
            )}
            {vendor.billingAddress.street && (
              <>
                <div className="text-gray-500 font-medium">Billing address</div>
                <div className="text-gray-700">{vendor.billingAddress.street}, {vendor.billingAddress.city}, {vendor.billingAddress.state} {vendor.billingAddress.zip}</div>
              </>
            )}
            {vendor.spendThreshold && (
              <>
                <div className="text-gray-500 font-medium">Spend threshold</div>
                <div className="text-gray-700">{fmt(vendor.spendThreshold.amount)} ({vendor.spendThreshold.type})</div>
              </>
            )}
            {vendor.vendorId && (
              <>
                <div className="text-gray-500 font-medium">Vendor ID</div>
                <div className="text-gray-700">{vendor.vendorId}</div>
              </>
            )}
            <div className="text-gray-500 font-medium">W9 on file</div>
            <div className="text-gray-700">{vendor.w9OnFile ? 'Yes' : 'No'}</div>
            <div className="text-gray-500 font-medium">Insurance cert</div>
            <div className="text-gray-700">{vendor.insuranceCertOnFile ? 'Yes' : 'No'}</div>
            {vendor.comments && (
              <>
                <div className="text-gray-500 font-medium col-span-2 mt-1">Comments</div>
                <div className="text-gray-700 col-span-2">{vendor.comments}</div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Child vendors */}
      {expanded && children && children.length > 0 && (
        <div className="px-4 pb-4 bg-gray-50 flex flex-col gap-2">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1">Sub-vendors</div>
          {children.map(child => (
            <VendorCard key={child.id} vendor={child} onEdit={onEdit} onArchive={onArchive} canEdit={canEdit} isChild />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function VendorsPage() {
  const { role, canEditVendors, canSeeVendor } = useVendorRole();

  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [showModal, setShowModal] = useState(false);
  const [editVendor, setEditVendor] = useState<Vendor | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<Vendor | null>(null);

  const load = () => setVendors(vendorStore.list());

  useEffect(() => { load(); }, []);

  const openAdd  = () => { setEditVendor(null); setShowModal(true); };
  const openEdit = (v: Vendor) => { setEditVendor(v); setShowModal(true); };
  const closeModal = () => { setShowModal(false); setEditVendor(null); };

  const handleArchive = (v: Vendor) => setArchiveTarget(v);
  const confirmArchive = () => {
    if (!archiveTarget) return;
    vendorStore.archive(archiveTarget.id);
    toast.success(`${archiveTarget.name} archived`);
    setArchiveTarget(null);
    load();
  };

  // Build parent/child tree
  const filtered = vendors
    .filter(v => canSeeVendor(v.categories))
    .filter(v => !search || v.name.toLowerCase().includes(search.toLowerCase()) || (v.vendorId ?? '').toLowerCase().includes(search.toLowerCase()))
    .filter(v => !filterCategory || v.categories.includes(filterCategory as VendorCategory))
    .filter(v => !filterStatus || v.status === filterStatus);

  const parents  = filtered.filter(v => !v.parentVendorId);
  const childMap = new Map<string, Vendor[]>();
  filtered.filter(v => v.parentVendorId).forEach(v => {
    const list = childMap.get(v.parentVendorId!) ?? [];
    list.push(v);
    childMap.set(v.parentVendorId!, list);
  });

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex justify-between items-start mb-5">
        <div>
          <h1 className="text-[22px] font-semibold text-gray-900 mb-0.5">Vendors</h1>
          <p className="text-sm text-gray-500">{filtered.length} vendor{filtered.length !== 1 ? 's' : ''} · Role: {role}</p>
        </div>
        {canEditVendors && (
          <Btn label="+ Add Vendor" onClick={openAdd} variant="primary" />
        )}
      </div>

      {/* Filters */}
      <div className="flex gap-2.5 mb-5">
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search vendors or ID..."
          className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-[#1a5c2a]/20 focus:border-[#1a5c2a]"
        />
        <select
          value={filterCategory}
          onChange={e => setFilterCategory(e.target.value)}
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white outline-none"
        >
          <option value="">All Categories</option>
          {ALL_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select
          value={filterStatus}
          onChange={e => setFilterStatus(e.target.value)}
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white outline-none"
        >
          <option value="">All Statuses</option>
          {VENDOR_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {/* Vendor list */}
      {vendors.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <div className="text-4xl mb-3">🏢</div>
          <div className="text-sm">No vendors yet. Add your first vendor to get started.</div>
        </div>
      ) : parents.length === 0 ? (
        <div className="text-center py-12 text-gray-400 text-sm">No vendors match your filters.</div>
      ) : (
        <div className="flex flex-col gap-3">
          {parents.map(v => (
            <VendorCard
              key={v.id}
              vendor={v}
              children={childMap.get(v.id)}
              onEdit={openEdit}
              onArchive={handleArchive}
              canEdit={canEditVendors}
            />
          ))}
        </div>
      )}

      {/* Modal overlay */}
      {(showModal || archiveTarget) && (
        <div
          className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
          onClick={() => { closeModal(); setArchiveTarget(null); }}
        >
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-[600px] p-6"
            style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}
            onClick={e => e.stopPropagation()}
          >
            {showModal && (
              <VendorModal
                vendor={editVendor}
                allVendors={vendors}
                onSave={() => { closeModal(); load(); }}
                onCancel={closeModal}
              />
            )}
            {archiveTarget && !showModal && (
              <ArchiveDialog
                vendor={archiveTarget}
                onConfirm={confirmArchive}
                onCancel={() => setArchiveTarget(null)}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
